import { ArrowRight2 } from 'iconsax-react';
import { pick } from 'lodash-es';
import { type CSSProperties, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useGroupMembers from '@/hooks/groups/use-group-members';
import useContentClamp from '@/hooks/use-content-clamp';
import cn from '@/utils/merge-class-names';
import type { ContentCommonProps } from './content-common-props';
import { MessageQuote } from './message-quote';
import { ScriptType, transformSpecialScripts } from './transform-scripts';

type ContentTextProps = ContentCommonProps & {
  type?: 'quote' | 'clamped';
};

export default function ContentText({ message, type }: ContentTextProps) {
  const isGroup = 'GroupID' in message && !!message.GroupID;
  const { t } = useTranslation();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const { resetSelectedMessages } = useConversationStore();
  const { handleInteraction: onInteraction } = useConversationHandlers();
  const { messageLineClamp, isContentClamped } = useContentClamp(contentRef);
  const { members } = useGroupMembers(isGroup ? message.GroupID : null);

  const content = useMemo(() => {
    const mentionString = 'AtIDs' in message ? (message.AtIDs ?? '') : '';
    const atMembers = Object.values(pick(members, mentionString.split('|').map(Number))).map((m) => ({
      id: m.UserID,
      groupId: m.GroupID,
      groupName: m.GroupMemberName,
      userName: m.UserNickName,
    }));

    return transformSpecialScripts(
      message.ID,
      message.Message,
      atMembers,
      isGroup
        ? [ScriptType.LineBreak, ScriptType.Link, ScriptType.Emoticon, ScriptType.Mention]
        : [ScriptType.LineBreak, ScriptType.Link, ScriptType.Emoticon],
    );
  }, [members, message]);

  const handleViewText = () => {
    resetSelectedMessages([message]);
    onInteraction('preview');
  };

  return (
    <div
      key={`message-${message.ID}-text`}
      className='chat-bubble items-center break-all text-left group-data-receiver:chat-bubble-plain group-data-sender:chat-bubble-primary'>
      {message.Quote && (
        <MessageQuote
          className='py-1.5 px-2 mb-1 w-full'
          quote={message.Quote}
        />
      )}

      <p
        ref={contentRef}
        style={
          {
            '--line-clamp': type === 'clamped' ? messageLineClamp : undefined,
          } as CSSProperties
        }
        className={cn(
          'break-words desktop:break-keep desktop:line-clamp-none line-break-anywhere',
          type === 'clamped' ? 'line-clamp-var' : 'line-clamp-3 medium:line-clamp-5',
        )}>
        {content}
      </p>

      {isContentClamped && (
        <button
          type='button'
          className='desktop:hidden w-full'
          onClick={handleViewText}>
          <hr className='my-2' />
          <div className='flex justify-between'>
            <p className='text-xs font-normal'>{t('conversation.message.see-all')}</p>
            <ArrowRight2 className='aspect-square size-5' />
          </div>
        </button>
      )}
    </div>
  );
}
